import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminAPI } from '../../services/apiClient';
import { toast } from 'react-hot-toast';
import '../../styles/fonts.css';
import '../../styles/AIChat.css';
import './AdminForms.css'; // Using shared admin form styles
import '../../styles/b_admin_styling.css';
import './ui/b_ui.css';
import { BFormField, BTextInput, BSelect, BCheckbox } from './ui';
import BButton from './ui/BButton';

const CreateBusinessAdmin = ({ presetBusinessId = null }) => {
    const navigate = useNavigate();
    const [name, setName] = useState('');
    const [username, setUsername] = useState('');
    const [email, setEmail] = useState('');
    const [selectedBusinessId, setSelectedBusinessId] = useState(presetBusinessId ? String(presetBusinessId) : '');
    const [businesses, setBusinesses] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingBusinesses, setIsLoadingBusinesses] = useState(true);
    const [deliveryMode, setDeliveryMode] = useState('automated');
    const [showActivationModal, setShowActivationModal] = useState(false);
    const [activationDetails, setActivationDetails] = useState(null);

    // Define simplified permissions for a Business Admin (survey platform only)
    const availableAdminPermissions = {
        can_create_surveys: "Create Surveys",
        can_edit_surveys: "Edit Surveys",
        can_delete_surveys: "Delete Surveys",
        can_view_survey_analytics: "View Survey Analytics"
    };

    const [adminPermissions, setAdminPermissions] = useState(
        Object.keys(availableAdminPermissions).reduce((acc, key) => {
            acc[key] = false;
            return acc;
        }, {})
    );

    useEffect(() => {
        // If we have a preset businessId, we only need to fetch that specific business for label display
        const fetchBusinesses = async () => {
            setIsLoadingBusinesses(true);
            try {
                const resp = await adminAPI.listBusinesses();
                const approvedBusinesses = resp.data || [];
                setBusinesses(approvedBusinesses);

                // If no businesses found after fetch, alert user
                if (approvedBusinesses.length === 0) {
                    toast.error("No businesses found. Please create a business first to assign an admin.", { duration: 5000});
                }
            } catch (error) {
                console.error("Error fetching businesses:", error);
                toast.error(error.response?.data?.error || 'Failed to fetch businesses.');
                setBusinesses([]);
            } finally {
                setIsLoadingBusinesses(false);
            }
        };
        fetchBusinesses();
    }, []);

    const handleAdminPermissionChange = (permissionKey) => {
        setAdminPermissions(prev => ({
            ...prev,
            [permissionKey]: !prev[permissionKey]
        }));
    };

    const handleSelectAllAdminPermissions = (e) => {
        const checked = e.target.checked;
        setAdminPermissions(
            Object.keys(availableAdminPermissions).reduce((acc, key) => {
                acc[key] = checked;
                return acc;
            }, {})
        );
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        toast.success('Copied to clipboard!');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!name.trim() || !email.trim() || !selectedBusinessId) {
            toast.error('Name, Email, and Business selection are required.');
            return;
        }
        if (businesses.length === 0) {
            toast.error("Cannot create a business admin without an existing business. Please create a business first.");
            return;
        }

        setIsLoading(true);

        const businessAdminData = {
            name,
            email,
            username: username.trim() || undefined,
            business_id: presetBusinessId ? parseInt(presetBusinessId) : parseInt(selectedBusinessId),
            business_admin_permissions: adminPermissions,  // Fixed: use correct key
            send_activation_email: deliveryMode === 'automated',
            delivery_mode: deliveryMode
        };

        try {
            const response = await adminAPI.createBusinessAdmin(businessAdminData);
            if (response.data.activation) {
                setActivationDetails(response.data.activation);
                setShowActivationModal(true);
                if (response.data.activation.email_sent) {
                    toast.success('Business admin created and activation email sent.');
                } else {
                    toast.success('Business admin created. Copy and share the activation link.');
                }
            } else {
                toast.success(response.data.message || 'Business admin created successfully.');
                handleCloseActivationModal();
            }
        } catch (error) {
            console.error("Error creating business admin:", error);
            toast.error(error.response?.data?.error || error.message || 'Failed to create business admin.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleCloseActivationModal = () => {
        setShowActivationModal(false);
        if (presetBusinessId) {
            navigate(`/admin/business/${presetBusinessId}/admins/manage`);
        } else {
            navigate('/admin/users/manage');
        }
    };

    return (
        <>
            <div className="newmain-content33 admin-form-container layout-admin-form">
                <div className="form-container-card"> 
                    <div className="form-header">
                        <h1 className="chat-title">Create New Business Admin</h1>
                        <p className="chat-subtitle">Assign an administrator to a specific business with defined permissions.</p>
                    </div>

                    <form onSubmit={handleSubmit} className="admin-form">
                        <BFormField label="Full Name" required>
                            <BTextInput
                                id="adminName"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder="Enter admin's full name"
                                required
                            />
                        </BFormField>

                        <BFormField label="Username (optional)">
                            <BTextInput
                                id="adminUsername"
                                value={username}
                                onChange={(e) => setUsername(e.target.value)}
                                placeholder="Defaults to email prefix if empty"
                            />
                        </BFormField>

                        <BFormField label="Email" required>
                            <BTextInput
                                type="email"
                                id="adminEmail"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="Enter admin's email"
                                required
                            />
                        </BFormField>

                        <div className="form-note" style={{marginTop: '10px', padding: '12px', backgroundColor: '#f0f9ff', borderRadius: '8px', border: '1px solid #bae6fd'}}>
                            <strong>Note:</strong> New admins set their password using a secure activation link (expires in 7 days). Their 12-month licence starts on activation.
                        </div>

                        <BFormField label="Assign to Business" required>
                            {presetBusinessId ? (
                                <p><strong>{businesses.find((b) => String(b.id) === String(presetBusinessId))?.name || `Business ID ${presetBusinessId}`}</strong></p>
                            ) : isLoadingBusinesses ? (
                                <p>Loading businesses...</p>
                            ) : businesses.length > 0 ? (
                                <BSelect
                                    id="assignedBusiness"
                                    value={selectedBusinessId}
                                    onChange={(e) => setSelectedBusinessId(e.target.value)}
                                    required
                                >
                                    <option value="">-- Select a Business --</option>
                                    {businesses.map(biz => (
                                        <option key={biz.id} value={biz.id}>
                                            {biz.name} (Tier: {biz.tier})
                                        </option>
                                    ))}
                                </BSelect>
                            ) : (
                                <p style={{color: 'red'}}>No businesses available. Create a business first.</p>
                            )}
                        </BFormField>

                        <div className="newform-group">
                            <label>Business Admin Permissions</label>
                            <div className="permissions-grid">
                                 <div className="permission-item-header">
                                      <BCheckbox 
                                         id="select_all_permissions_admin"
                                         checked={Object.values(adminPermissions).every(Boolean)}
                                         onChange={handleSelectAllAdminPermissions}
                                         label={<span style={{fontWeight: 'bold'}}>Select All</span>}
                                      />
                                </div>
                                {Object.entries(availableAdminPermissions).map(([key, label]) => (
                                    <button
                                        key={key}
                                        type="button"
                                        className={`permission-pill ${adminPermissions[key] ? 'permission-pill--selected' : ''}`}
                                        onClick={() => selectedBusinessId && handleAdminPermissionChange(key)}
                                        disabled={!selectedBusinessId}
                                    >
                                        {label}
                                    </button>
                                ))}
                            </div>
                            <small className="form-note" style={{marginTop: '10px', display: 'block'}}>
                                Note: A Business Admin can only be granted permissions that are also enabled for the selected Business. 
                                If a permission is selected here but not active for the Business, it will not be assigned to the admin.
                            </small>
                            { !selectedBusinessId && <small style={{color: 'orange', display: 'block', marginTop: '5px'}}>Select a business to enable permission assignment.</small> }
                        </div>

                        <BFormField label="Activation Delivery">
                            <div style={{ display: 'grid', gap: '10px' }}>
                                <label style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                    <input
                                        type="radio"
                                        name="deliveryMode"
                                        value="automated"
                                        checked={deliveryMode === 'automated'}
                                        onChange={(e) => setDeliveryMode(e.target.value)}
                                    />
                                    Send automated activation email from info@eclipseer.com
                                </label>
                                <label style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                    <input
                                        type="radio"
                                        name="deliveryMode"
                                        value="manual"
                                        checked={deliveryMode === 'manual'}
                                        onChange={(e) => setDeliveryMode(e.target.value)}
                                    />
                                    Generate link only (I will copy and send manually)
                                </label>
                            </div>
                        </BFormField>

                        <div className="form-actions">
                            <BButton type="button" variant="secondary" onClick={() => navigate('/admin')} disabled={isLoading}>
                                Cancel
                            </BButton>
                            <BButton type="submit" variant="primary" disabled={isLoading || isLoadingBusinesses || businesses.length === 0}>
                                {isLoading ? 'Creating Admin...' : 'Create Business Admin'}
                            </BButton>
                        </div>
                    </form>
                </div>
            </div>

            {showActivationModal && activationDetails && (
                <div className="modal-overlay" style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000
                }}>
                    <div className="modal-content" style={{
                        backgroundColor: 'white',
                        padding: '30px',
                        borderRadius: '12px',
                        maxWidth: '500px',
                        width: '90%',
                        boxShadow: '0 10px 40px rgba(0,0,0,0.2)'
                    }}>
                        <h2 style={{ marginTop: 0, color: '#10b981' }}>Business Admin Created</h2>
                        <p style={{ color: '#666' }}>
                            Share this activation link with the business admin. The link is bound to the email and expires in 7 days.
                        </p>
                        
                        <div style={{ backgroundColor: '#f3f4f6', padding: '20px', borderRadius: '8px', marginTop: '20px' }}>
                            <div style={{ marginBottom: '15px' }}>
                                <label style={{ fontWeight: 'bold', color: '#374151', display: 'block', marginBottom: '5px' }}>Account email:</label>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <code style={{ flex: 1, padding: '8px', backgroundColor: 'white', borderRadius: '4px', border: '1px solid #d1d5db' }}>
                                        {activationDetails.email}
                                    </code>
                                    <BButton type="button" variant="secondary" onClick={() => copyToClipboard(activationDetails.email)}>
                                        Copy
                                    </BButton>
                                </div>
                            </div>
                            
                            <div style={{ marginBottom: '15px' }}>
                                <label style={{ fontWeight: 'bold', color: '#374151', display: 'block', marginBottom: '5px' }}>Activation link:</label>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <code style={{ flex: 1, padding: '8px', backgroundColor: 'white', borderRadius: '4px', border: '1px solid #d1d5db', fontFamily: 'monospace' }}>
                                        {activationDetails.activation_link}
                                    </code>
                                    <BButton type="button" variant="secondary" onClick={() => copyToClipboard(activationDetails.activation_link)}>
                                        Copy
                                    </BButton>
                                </div>
                            </div>

                            <div style={{ color: '#4b5563', fontSize: '14px' }}>
                                <div><strong>Link expires:</strong> {new Date(activationDetails.expires_at).toLocaleString()}</div>
                                <div><strong>Email delivery:</strong> {activationDetails.email_sent ? 'Sent automatically' : 'Manual send selected'}</div>
                            </div>
                        </div>
                        
                        <div style={{ marginTop: '25px', textAlign: 'right' }}>
                            <BButton type="button" variant="primary" onClick={handleCloseActivationModal}>
                                Done
                            </BButton>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default CreateBusinessAdmin; 
